import { useState } from "react";
import axios from "axios";
import { Send, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import PageHeader from "../components/PageHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", company: "", country: "", email: "", phone: "", products: "", packaging: "", quantity: "", destination: "", message: "" };

export default function RequestQuotePage() {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, source: "quote" });
      setDone(true);
      toast.success("Inquiry received.");
    } catch {
      toast.error("Could not submit. Please email info@deeprootsspice.com directly.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader
        eyebrow="Request A Quote"
        title="Tell us about your requirements."
        italicWord="requirements"
        subtitle="Complete the form below with as much detail as you can share. Our export team will respond with pricing, samples and shipping options within 24 business hours."
      />

      <section className="py-24 md:py-32 bg-brand-ivory">
        <div className="container-luxe max-w-4xl">
          {done ? (
            <Reveal className="p-12 text-center bg-brand-ivory-warm border border-brand-gold/40">
              <div className="w-16 h-16 rounded-full bg-brand-green/10 border border-brand-green flex items-center justify-center mx-auto mb-6">
                <Check className="w-7 h-7 text-brand-green" />
              </div>
              <h3 className="font-serif text-4xl text-brand-charcoal">Inquiry received.</h3>
              <p className="mt-4 text-brand-charcoal/65 max-w-lg mx-auto">Thank you. Our export team will review your requirements and respond within 24 business hours with pricing, samples and shipping options.</p>
              <Link to="/" className="mt-10 luxe-btn-outline text-brand-green border-brand-green hover:bg-brand-green hover:text-brand-ivory inline-flex" data-testid="quote-page-home">
                Back to Home <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submit} className="p-10 md:p-14 bg-brand-ivory-warm border border-brand-charcoal/10" data-testid="quote-page-form">
                <div className="grid md:grid-cols-2 gap-x-10 gap-y-3">
                  {[
                    ["name", "Full Name*", "text", true],
                    ["company", "Company", "text", false],
                    ["email", "Email*", "email", true],
                    ["phone", "Phone", "tel", false],
                    ["country", "Your Country", "text", false],
                    ["destination", "Destination Port / Country", "text", false],
                  ].map(([k, label, type, req]) => (
                    <div key={k}>
                      <label className="overline text-brand-charcoal/50 block mb-1">{label}</label>
                      <input required={req} type={type} value={form[k]} onChange={set(k)} className="input-editorial" data-testid={`quote-page-${k}`} />
                    </div>
                  ))}
                  <div className="md:col-span-2">
                    <label className="overline text-brand-charcoal/50 block mb-1">Required Products</label>
                    <input placeholder="e.g. Turmeric Powder, Green Cardamom, Garam Masala" value={form.products} onChange={set("products")} className="input-editorial" data-testid="quote-page-products" />
                  </div>
                  <div>
                    <label className="overline text-brand-charcoal/50 block mb-1">Packaging Preference</label>
                    <input placeholder="25kg PP bags, retail SUP, etc." value={form.packaging} onChange={set("packaging")} className="input-editorial" data-testid="quote-page-packaging" />
                  </div>
                  <div>
                    <label className="overline text-brand-charcoal/50 block mb-1">Quantity</label>
                    <input placeholder="e.g. 5 MT" value={form.quantity} onChange={set("quantity")} className="input-editorial" data-testid="quote-page-quantity" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="overline text-brand-charcoal/50 block mb-1">Message</label>
                    <textarea rows={4} value={form.message} onChange={set("message")} className="input-editorial resize-none" data-testid="quote-page-message" />
                  </div>
                </div>
                <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <p className="text-[12px] text-brand-charcoal/50 max-w-md">By submitting, you agree to be contacted by our export team regarding this inquiry.</p>
                  <button type="submit" disabled={busy} className="luxe-btn-primary disabled:opacity-60" data-testid="quote-page-submit">
                    {busy ? "Sending..." : "Send Inquiry"} <Send className="w-3.5 h-3.5" />
                  </button>
                </div>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
}
